/**
 * ComingSoon — a premium, hopeful "this feature is on the way" card. Used by the
 * shell to present not-yet-shipped screens (Chart of Accounts, Periods, Reports,
 * Migration) without touching their underlying implementations. Positive,
 * customer-pulling copy + a layered glowing medallion, a gradient badge, a
 * feature preview list, and a "notify me" affordance.
 */
import React, { useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import SkIcon from './skIcon';
import { showToast } from './skToast';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
const CTA_A = '#6366f1';
const CTA_B = '#8b5cf6';

export interface ComingSoonProps {
  icon: string;
  title: string;
  blurb: string;
  features: string[];
  eta?: string;
}

const ComingSoon: React.FC<ComingSoonProps> = ({ icon, title, blurb, features, eta = 'Launching soon' }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide } = useResponsive();
  const [hover, setHover] = useState(false);

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      <View
        style={{
          maxWidth: 640,
          width: '100%',
          alignSelf: 'center',
          marginTop: isWide ? 24 : 8,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: 20,
          overflow: 'hidden',
          alignItems: 'center',
          paddingVertical: 44,
          paddingHorizontal: 28,
          ...theme.shadow('md'),
          ...web({ backgroundImage: `radial-gradient(circle at 50% -10%, ${colors.accentSoft}, ${colors.card} 60%)` }),
        }}
      >
        {/* Gradient "coming soon" badge */}
        <View
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, marginBottom: 22,
            backgroundColor: CTA_A,
            ...web({ backgroundImage: `linear-gradient(135deg, ${CTA_A}, ${CTA_B})`, boxShadow: `0 6px 16px -5px ${CTA_B}aa` }),
          }}
        >
          <Text style={{ fontSize: 12, color: '#fff' }}>✦</Text>
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '700', letterSpacing: 1.2, color: '#fff', textTransform: 'uppercase' }}>Coming soon</Text>
        </View>

        {/* Layered glowing medallion */}
        <View
          style={{
            width: 108, height: 108, borderRadius: 34, alignItems: 'center', justifyContent: 'center', marginBottom: 22,
            backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine,
            ...web({ backgroundImage: `radial-gradient(circle at 50% 30%, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 18px 44px -12px ${colors.accent}55` }),
          }}
        >
          <View
            style={{
              width: 64, height: 64, borderRadius: 21, alignItems: 'center', justifyContent: 'center',
              backgroundColor: colors.card, borderWidth: 1, borderColor: colors.accentLine,
              ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 8px 20px -6px ${colors.accent}66` }),
            }}
          >
            <SkIcon name={icon} size={28} color={colors.accentInk} strokeWidth={2} />
          </View>
        </View>

        <Text style={{ fontFamily: font.sansBold, fontSize: 23, fontWeight: '700', color: colors.ink, letterSpacing: -0.5, textAlign: 'center' }}>{title}</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 14, color: colors.ink3, textAlign: 'center', lineHeight: 22, maxWidth: 460, marginTop: 10 }}>{blurb}</Text>

        {/* Feature preview list */}
        <View style={{ width: '100%', maxWidth: 420, gap: 10, marginTop: 26 }}>
          {features.map((f) => (
            <View key={f} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 11, paddingHorizontal: 14, borderRadius: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper }}>
              <View style={{ width: 26, height: 26, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.successSoft}, ${colors.card})` }) }}>
                <SkIcon name="check" size={13} color={colors.success} strokeWidth={2.6} />
              </View>
              <Text style={{ flex: 1, fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>{f}</Text>
            </View>
          ))}
        </View>

        {/* Notify CTA + ETA */}
        <Pressable
          onPress={() => showToast('success', 'You’re on the list 🎉', 'We’ll let you know the moment it goes live.')}
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 8, height: 42, paddingHorizontal: 20, borderRadius: 11, marginTop: 28, backgroundColor: CTA_A,
            ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: hover ? `0 12px 26px -6px ${CTA_B}bb` : `0 7px 16px -5px ${CTA_B}88`, transform: hover ? 'translateY(-1px)' : 'translateY(0)', transition: 'transform .14s ease, box-shadow .14s ease', cursor: 'pointer' }),
          }}
          {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
        >
          <SkIcon name="bell" size={15} color="#fff" strokeWidth={2.2} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13.5, fontWeight: '600', color: '#fff' }}>Notify me when it’s ready</Text>
        </Pressable>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 14 }}>
          <SkIcon name="clock" size={12} color={colors.ink4} strokeWidth={2} />
          <Text style={{ fontFamily: font.sansMedium, fontSize: 11.5, color: colors.ink4, letterSpacing: 0.2 }}>{eta} · crafted with care for you</Text>
        </View>
      </View>
    </View>
  );
};

export default ComingSoon;
